import {
  popoverTooltipContentVariants,
  tooltipButtonVariants,
} from '@common/cva/popoverTooltipButton';
import Image from '@react/shared/components/Image';
import InlineTour from '@react/shared/components/InlineTour';
import { Agent } from '@sly/core/types';
import {
  SENIORLY_AWARD_IMAGE_SRC_AGENTS,
  SENIORLY_AWARD_IMAGE_SRC_AGENTS_PLATINUM,
  SENIORLY_AWARD_YEAR_AGENTS,
} from '@sly/frontend/common/constants/awardText';
import { phoneFormatter } from '@sly/frontend/common/helpers/phone';
import ExpertHelpCta from '@sly/frontend/react/cta/ExpertHelpCta';
import CuratedReviews from '@sly/frontend/react/search/components/Curated/CuratedReviews';
import IconWithText from '@sly/frontend/react/shared/components/CheckmarkWithText';
import Footer from '@sly/frontend/react/shared/components/Footer';
import HeaderContainer from '@sly/frontend/react/shared/components/Header/Header.container';
import Video from '@sly/frontend/react/shared/components/Video';
import {
  Award,
  Book,
  Care,
  Checklist,
  Checkmark,
  Favorite,
  HomeLarge,
  Money,
  MoveIn,
  Phone,
} from '@sly/icons/react';
import { BestCare, ChatStroke } from '@sly/icons/react';
import { Education } from '@sly/icons/react';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';
import AgentCities from 'sly/agentsAgentPage/AgentProfilePage/AgentCities';
import AgentSeo from 'sly/agentsAgentPage/AgentProfilePage/AgentSeo';
import { cva, VariantProps } from 'sly/lib/cva';
import { usePrefetch } from 'sly/services/api';
import { Button, Heading, Link } from 'sly/system';

const ReactTooltip = dynamic(() => import('react-tooltip'), {
  ssr: false,
});

import { cn } from '@sly/frontend/common/lib/clsxm';
import dynamic from 'next/dynamic';
import AgentProfilePageHeader from 'sly/agentsAgentPage/AgentProfilePage/AgentProfilePageHeader';
import { FAQContent } from 'sly/homePage/components/SeniorlyFAQs';

import PRMRLogo from './PRMRLogo';

const services = [
  {
    title: 'Consultation',
    description:
      'The initial consultation is key to finding the right senior living community. Your Seniorly Premier Advisor will ask thoughtful questions to understand your loved one’s needs, preferences and priorities.',
    icon: ChatStroke,
  },
  {
    title: 'Assessment',
    description:
      'The care needs assessment is essential in finding a senior living community that feels like home—both today and in the future.',
    icon: BestCare,
  },
  {
    title: 'Personalized list of options',
    description:
      "Your Premier Advisor will create a customized list of communities, ensuring they match your loved one's needs  - and help you make an informed decision.",
    icon: Checklist,
  },
  {
    title: 'Education',
    description:
      'The most important part of making a confident decision is understanding the options, and your Seniorly Premier Advisor provides the information and support you need - every step of the way.',
    icon: Book,
  },
  {
    title: 'Financial guidance',
    description:
      'Our Premier Advisors provide clear, compassionate support to help families understand the programs available to them and what they can truly afford, ensuring peace of mind throughout the process.',
    icon: Money,
  },
  {
    title: 'Move-in assistance',
    description:
      'With a deep network of trusted partners, our Premier Advisors are here to support every step of the move-in process, ensuring a seamless and comfortable transition.',
    icon: MoveIn,
  },
];

interface AboutFieldProps {
  title: ReactNode;
  Icon: React.ElementType;
  children: React.ReactNode;
}

const section = cva('flex flex-col py-8 lg:py-12', {
  variants: {
    background: {
      white: 'bg-white',
      harvest: 'bg-light-base',
      viridian: 'bg-viridian-lighter-80',
    },
    rounded: {
      true: 'rounded-xl lg:rounded-none',
    },
    center: {
      true: 'items-center justify-center',
    },
    top: {
      true: 'mt-2 lg:mt-0',
    },
    bottom: {
      true: 'border-b',
    },
  },
  defaultVariants: {
    background: 'white',
    center: true,
  },
});

const sectionContainer = cva('flex flex-col container', {
  variants: {
    center: {
      true: 'items-center justify-center',
    },
  },
  defaultVariants: {
    center: true,
  },
});

type SectionProps = VariantProps<typeof section> & {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  center?: boolean;
};

const Section = ({
  children,
  className,
  containerClassName,
  center,
  ...rest
}: SectionProps) => {
  return (
    <div className={section({ ...rest, className })}>
      <div
        className={sectionContainer({ className: containerClassName, center })}
      >
        {children}
      </div>
    </div>
  );
};

const AboutField = ({ title, Icon, children }: AboutFieldProps) => {
  return (
    <div className='flex flex-row gap-4'>
      <Icon />
      <div>
        <div className='font-t-xs-azo mb-2'>{title}</div>
        {children}
      </div>
    </div>
  );
};

const PremierAgentPage = () => {
  const { query } = useRouter();
  const {
    requestInfo: { normalized },
  } = usePrefetch('getAgent', {
    id: query.slug,
  });

  const agent = normalized as Agent;

  const bio = agent?.info?.bio?.replace(/\n/g, '<br />');

  const info = agent?.info;

  const phone = agent?.twilioNumber?.numbers?.[0];
  const appointmentLink = agent?.info?.appointmentLink;

  const faqs = agent?.info?.faqs;

  return (
    <>
      <AgentSeo agent={agent} />
      <AgentProfilePageHeader agent={agent} />
      <HeaderContainer />
      <main className='bg-harvest-lighter-90'>
        <Section
          background='harvest'
          containerClassName='lg:flex-row-reverse lg:gap-36'
        >
          <div className='relative mb-6 aspect-1 max-h-[360px] w-full max-w-[360px]'>
            <Image
              className='h-auto w-full rounded-lg'
              path={agent?.gallery?.images?.[0]?.path}
              alt={agent?.name}
              objectFit='cover'
            />
            <div className='prmr-profile-gradiant absolute h-full w-full rounded-xl opacity-60' />
            <PRMRLogo className='absolute bottom-4 right-4 z-10 w-24 text-white' />
          </div>
          <div className='flex flex-col items-center'>
            <h1 className='font-t-xl mb-4 self-start'>
              Hi, I&apos;m {info?.displayName?.split(' ')[0]}{' '}
            </h1>
            <p className='font-b-xl mb-8'>
              As your dedicated senior living advisor, I&apos;m here to help you
              find the right senior living option for your loved one—a place
              that feels just like home
            </p>

            <div className='flex w-full flex-col items-center gap-5 lg:flex-row'>
              {appointmentLink ? (
                <Button
                  variant='prmr'
                  palette='primary'
                  onClick={() => {
                    window.open(appointmentLink, '_blank');
                  }}
                  eventProps={{
                    location: 'agent profile page',
                    text: 'Schedule a free consultation',
                  }}
                >
                  Schedule a free consultation
                </Button>
              ) : (
                <ExpertHelpCta
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  className='w-full lg:w-auto'
                  eventProps={{
                    location: 'agent profile page',
                    text: 'Schedule a free consultation',
                  }}
                  agent={agent}
                  ctaProps={{
                    text: 'Schedule a free consultation',
                    variant: 'prmr',
                    palette: 'primary',
                  }}
                />
              )}
              {phone && (
                <Link className='font-t-xs-azo' href={`tel:${phone}`}>
                  {phoneFormatter(phone, true)}
                </Link>
              )}
            </div>
          </div>
        </Section>
        <Section background='white' center={false} rounded>
          <div className='flex flex-col gap-6 lg:grid lg:grid-cols-2 lg:rounded-lg lg:border lg:py-12 lg:px-8'>
            <div
              className={cn(
                'flex flex-grow-0 flex-col gap-6',
                !info?.video && 'lg:col-span-2'
              )}
            >
              <h2 className='font-t-l'>About me</h2>
              <p
                className='font-b-m'
                dangerouslySetInnerHTML={{ __html: bio }}
              />
            </div>
            {info?.video && (
              <Video
                className='!w-full rounded-lg'
                title='About me'
                lazyLoad={false}
                iframe
                url={info.video}
              />
            )}
            {info?.serviceArea?.operatingIn && (
              <AboutField title='Operating in' Icon={HomeLarge}>
                {info?.serviceArea?.operatingIn}
              </AboutField>
            )}
            {phone && (
              <AboutField title="Let's connect" Icon={Phone}>
                {phoneFormatter(phone, true)}
              </AboutField>
            )}
            {(info?.licenses?.length > 0 || info?.certificates?.length > 0) && (
              <AboutField title='Licenses & Certifications' Icon={Care}>
                {info?.licenses?.map((license) => license.licenseName)}
                {info?.certificates?.map((certificate) => (
                  <div key={certificate.certificateName}>
                    {certificate.certificateName}
                  </div>
                ))}
              </AboutField>
            )}
            {info?.education?.length > 0 && (
              <AboutField title='Education' Icon={Education}>
                {info?.education?.map((education) => (
                  <div key={education.instituteName}>
                    {education.instituteName}, {education.degree},{' '}
                    {education.specializedField}
                  </div>
                ))}
              </AboutField>
            )}
            {info?.knowsAbout?.length > 0 && (
              <AboutField title='My specialties' Icon={Favorite}>
                <div className='flex flex-col gap-1'>
                  {info?.knowsAbout?.map((knowsAbout) => (
                    <div key={knowsAbout.title}>{knowsAbout.title}</div>
                  ))}
                </div>
              </AboutField>
            )}
            {(info?.platinumAward || info?.award) && (
              <AboutField
                title={
                  <>
                    <div
                      className={tooltipButtonVariants({
                        intent: 'popover',
                      })}
                      data-tip
                      data-for='test'
                    >
                      Seniorly Top Advisor Award
                    </div>
                    <ReactTooltip
                      id='test'
                      type='light'
                      effect='solid'
                      multiline
                      clickable={true}
                    >
                      <div className={popoverTooltipContentVariants()}>
                        Advisors that received this honor have demonstrated
                        exceptional dedication to helping families with their
                        search of senior living.
                      </div>
                    </ReactTooltip>
                  </>
                }
                Icon={Award}
              >
                {(info?.platinumAward || info?.award) && (
                  <div className='mb-16 flex flex-row items-center gap-4'>
                    <Image
                      src={
                        info?.platinumAward
                          ? SENIORLY_AWARD_IMAGE_SRC_AGENTS_PLATINUM
                          : SENIORLY_AWARD_IMAGE_SRC_AGENTS
                      }
                      layout='raw'
                      width={80}
                      height={80}
                      alt={`${
                        info.platinumAward && 'Platinum'
                      } Top Advisor ${SENIORLY_AWARD_YEAR_AGENTS}`}
                    />
                  </div>
                )}
              </AboutField>
            )}
          </div>
        </Section>
        <Section background='white' top center={false} bottom>
          <div className='font-l-m mb-4 text-viridian-base'>My services</div>
          <h2 className='font-t-l mb-4'>
            Get free expert guidance every step of the way
          </h2>
          <p className='font-b-m mb-6 max-w-4xl'>
            Whether you need a senior living advisor, an elder care guide, or a
            senior housing consultant, our services streamline the process of
            senior living placement. We provide expert guidance, saving you time
            and stress by identifying the best senior housing options for your
            family - at the right price. Here&apos;s how we do it:
          </p>

          <div className='-mx-4 flex snap-x flex-row gap-6 overflow-x-auto scroll-smooth px-4 scrollbar-hide sm:grid sm:grid-cols-2 sm:gap-6 lg:grid-cols-3'>
            {services.map((service) => (
              <div
                key={service.title}
                className='flex min-w-[288px] snap-center flex-col rounded-xl border border-light-base bg-light-base p-6 sm:min-w-0'
              >
                <service.icon className='mb-6' />
                <h3 className='font-t-m mb-4'>{service.title}</h3>
                <p className='font-b-m'>{service.description}</p>
              </div>
            ))}
          </div>
        </Section>
        {!!agent?.reviews && (
          <Section background='white' top center={false}>
            <div className='font-l-m mb-4 text-viridian-base'>REVIEWS</div>
            <h2 className='font-t-l'>
              See what families say about working with me
            </h2>
            <CuratedReviews reviews={agent.reviews} />
          </Section>
        )}
        <Section
          background='viridian'
          containerClassName='gap-2 lg:grid lg:grid-cols-2 lg:gap-12'
          top
          center={false}
        >
          <div className='w-full'>
            <div className='font-l-m mb-2 text-viridian-base lg:mb-4'>
              Let&apos;s connect
            </div>
            <h2 className='font-t-l'>
              Ready to take the next step in finding a community you&apos;ll
              love?
            </h2>
            <div className='mt-6 hidden flex-col gap-4 lg:flex'>
              <IconWithText className='gap-4' Icon={Checkmark}>
                My services are completely free for families
              </IconWithText>
              <IconWithText className='gap-4' Icon={Checkmark}>
                Local expertise and knowledge
              </IconWithText>
              <IconWithText className='gap-4' Icon={Checkmark}>
                Over 10 years of experience and hundreds of families helped
              </IconWithText>
            </div>
          </div>

          <div className='-mx-4 sm:-mx-8'>
            <InlineTour
              agent={agent}
              className='mt-6 w-full rounded-lg bg-white p-8'
            />
          </div>
        </Section>
        <Section
          center
          background='harvest'
          containerClassName='gap-6 sm:py-20 sm:flex-row sm:gap-10 lg:px-0 max-w-3xl sm:items-start'
        >
          <PRMRLogo className='w-40 shrink-0 text-prmr-primary-base' />
          <div>
            <h2 className='font-t-l mb-6'>
              What is a Seniorly Premier Advisor?
            </h2>
            <p>
              Seniorly Premier Advisors are carefully vetted senior living
              experts, hand-picked by Seniorly for their exceptional service,
              deep community connections, and proven expertise. <br /> <br />
              With years of experience, they guide families to find the right
              senior living community, tailored to their loved one&apos;s needs,
              preferences, and budget. From exploring options to understanding
              costs, they provide personalized support every step of the way,
              making the process smooth and stress-free.
            </p>
          </div>
        </Section>
        {!!faqs && (
          <Section background='white' top center={false} bottom>
            <h2 className='font-t-l mb-6'>FAQs</h2>
            <FAQContent faqs={faqs} bottomBorder parseRaw />
          </Section>
        )}
        <Section background='white' top center={false}>
          <AgentCities
            separator={false}
            cities={info?.citiesServed}
            firstName={info?.displayName?.split(' ')[0]}
          />
          {agent?.info?.advisoryWorks?.length > 0 && (
            <div className='mb-12'>
              <Heading className='mb-4'>Senior Living Resources</Heading>
              <div className='grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'>
                {agent.info.advisoryWorks?.map((work) => {
                  return (
                    <Link
                      className='break-words text-viridian-base'
                      key={work.organizationName}
                      href={work.link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {work.organizationName} {work.role && `- ${work.role}`}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </Section>
      </main>
      <Footer />
    </>
  );
};

export default PremierAgentPage;
